/* eslint-disable react/button-has-type */
import React, {
  useEffect, useState, useCallback, useRef, useMemo,
} from 'react';
import * as PropTypes from 'prop-types';
import {
  Form, Row, Typography, Button, PageHeader, Spin, Result,
} from 'antd';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import ReactQuill from 'react-quill';
import ReactHtmlParser from 'react-html-parser';
import 'react-quill/dist/quill.snow.css';
import { withRouter, Redirect } from 'react-router-dom';
import '../Configure.css';
import { connect } from 'react-redux';
import routeLocations from '../../../../utility/route-locations';
import { requestUserGroupDetails } from '../../../../state/ui/configuration/company/companyUiActions';
import { selectUpdateGroupRequest } from '../../../../state/ui/configuration/company/companyUiReducer';
import { selectUserGroup } from '../../../../state/domain/company/companyReducer';
import { selectSessionUser } from '../../../../state/ui/application/sessionUiReducer';
import { userIsAdmin, displayAddressHTML, substituteEmailParams } from './user-util';
import ViaValidationAlert from '../../../../components/fields/ViaValidationAlert';
import './ContactDetails.css';
import { requestSpecificEmailContent, requestSaveEmailContent } from '../../../../state/ui/configuration/users/emailUiActions';
import { selectEmailContentMap } from '../../../../state/domain/emails/emailReducer';
import { LogoLocations } from '../../../../components/logo/ViaLogo';

const { Text } = Typography;


// More work than it was worth
function EditEmailContent({
  match, loadPageData, emailContentMap, history, saveContent, sessionUser, userGroup, loadUserGroup,
}) {
  // Currently I have locked this to only be inspection_report We have talked of allowing editing other emails
  const emailType = match.params.id.toUpperCase();
  useEffect(() => {
    if (!emailContentMap[emailType]) {
      loadPageData(emailType);
    }
    if (!userGroup) {
      loadUserGroup();
    }
  }, [loadPageData, emailContentMap, emailType, loadUserGroup, userGroup]);

  const intl = useIntl();

  // The quill instance contains all methods to interact with the text editor.
  const quillRef = useRef();

  // The html produced by the editor. Allows programatic changing of the email content with html (I.E restore default)
  const [currentHtml, setCurrentHtml] = useState(null);

  // Error denoting that the user attempted to save without a the emails required link
  const [missingLinkError, setMissingLinkError] = useState(null);

  // Set the default html when we first load the email content.
  useEffect(() => { if (!currentHtml) setCurrentHtml(emailContentMap[emailType] ? emailContentMap[emailType].emailContent : null); },
    [emailContentMap, setCurrentHtml, emailType, currentHtml]);


  const validateContent = useCallback(() => {
    // Quill delta, stores all the file information as a set of operations. See https://github.com/quilljs/delta
    const delta = quillRef.current.makeUnprivilegedEditor(quillRef.current.getEditor()).getContents();
    // Check they have a link with our substitution parameter (Has to have a https:// at the start otherwise JSoup will get rid of it on the server side)
    if (delta.ops.find(op => !!op.attributes && !!op.attributes.link && op.attributes.link === 'https://{inspection-report-link}' && op.insert.length > 2)) return true;
    return false;
  }, [quillRef]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();

    if (validateContent()) {
      saveContent({ emailType, emailContent: quillRef.current.makeUnprivilegedEditor(quillRef.current.getEditor()).getHTML() }, history);
    } else {
      // Currently the only validation is that it contains the link so we can just set the error here.
      // If more validation is added this should bemoved into validateContent()
      setMissingLinkError({ code: 400, errors: [intl.formatMessage({ id: 'substitutionMissing' })] });
    }
  }, [history, saveContent, quillRef, emailType, validateContent, intl]);

  // Put in example values for the header and footer.
  const visibleHeader = substituteEmailParams(
    emailContentMap[emailType] ? emailContentMap[emailType].header : null,
    userGroup ? {
      title: intl.formatMessage({ id: 'inspectionHeaderRegNo' }, { regNo: '{Reg Number}' }),
      companyName: userGroup.description,
      companyAddress: displayAddressHTML(userGroup.address),
      customerName: '{Customer Name}',
      locationEmail: '{Location Email}',
      logo: '<img width="180" src="{logo-data}" alt="" style="display:block; margin: 10px;"/>',
      logoUrl: `data:${userGroup.logoType};base64,${userGroup.logo}`,
    } : {},
  );
  const visibleFooter = substituteEmailParams(
    emailContentMap[emailType] ? emailContentMap[emailType].footer : null,
    {
      poweredByLogo: LogoLocations.POWERED_NORMAL_MOBLYX_BLUE_CIRCLE_WHITE_M,
      poweredByAlt: intl.formatMessage({ id: 'poweredBy' }),
      contentNotMoblyx: intl.formatMessage({ id: 'disclaimerStatement' }),
      emailUnwanted: intl.formatMessage({ id: 'emailUnwanted' }),
    },
  );

  // When modules is re-rendered the editor loses focus,
  // However it is depedent on insertName and addlink.
  // hence they are defined by a callback to prevent unnecessary recreation of modules.
  const insertName = useCallback(() => {
    // Create the editor in here, if it is defined outside it causes this function to change too often (see above)
    const editor = quillRef.current.getEditor();
    const cursorPosition = editor.getSelection().index;
    const higlightLength = editor.getSelection().length;

    // Replace the highlighted text
    editor.deleteText(cursorPosition, higlightLength);
    editor.insertText(cursorPosition, '{Customer Name}');

    // push cursor to the end of the customer name as we don't want them to edit it
    editor.setSelection(cursorPosition + '{Customer Name}'.length);
  }, [quillRef]);

  const addLink = useCallback(() => {
    // Create the editor in here, if it is defined outside it causes this function to change too often (see above)
    const editor = quillRef.current.getEditor();
    const cursorPosition = editor.getSelection().index;
    const higlightLength = editor.getSelection().length;

    if (higlightLength === 0) {
      // Nothing is highlighted, add clear text so they know what just happened.
      editor.insertText(cursorPosition, 'link to the full inspection', { link: 'https://{inspection-report-link}', underline: false });
      editor.setSelection(cursorPosition, 'link to the full inspection'.length);
    } else {
      // They highlighted something. They probably want that to be the link, so add the link to that text.
      editor.formatText(cursorPosition, higlightLength, { link: 'https://{inspection-report-link}' });
      editor.setSelection(cursorPosition + higlightLength);
    }
    // They just added the link, so get rid of this error if it is there
    setMissingLinkError(null);
  }, [quillRef, setMissingLinkError]);

  const addLocationLink = useCallback(() => {
    // Create the editor in here, if it is defined outside it causes this function to change too often (see above)
    const editor = quillRef.current.getEditor();
    const cursorPosition = editor.getSelection().index;
    const higlightLength = editor.getSelection().length;

    // Replace the highlighted text
    editor.deleteText(cursorPosition, higlightLength);
    editor.insertText(cursorPosition, '{Location Email}');

    // push cursor to the end of the customer name as we don't want them to edit it
    editor.setSelection(cursorPosition + '{Location Email}'.length);
  }, [quillRef]);

  // This thing is very picky (See above) Hence the use memo.
  const modules = useMemo(() => ({
    toolbar: {
      container: '#toolbar',
      handlers: {
        insertName,
        addLink,
        addLocationLink,
      },
    },
    clipboard: {
      matchVisual: false,
    },
  }), [addLink, insertName, addLocationLink]);

  /**
   * This also cannot be re-created on render (it loses all of its functionality with quill, god knows why)
   * Hence the error is passed in as props rather than just using it (since it is known in the upper scope)
   * Custom toolbar component including insert link and customer name button and dropdowns
   * @param props { showError } show error being truthy makes the inspection link button be highlighted in red.
   */
  const CustomToolbar = useCallback(({ showError }) => (
    <div id="toolbar">
        <button className="ql-bold" />
        <button className="ql-italic" />
        <button className="ql-underline" />
        <button className="ql-list" value="ordered" />
        <button className="ql-list" value="bullet" />
        <button className="ql-link" />
        <button
          id="via-button-quill"
          className="ql-insertName"
          style={{ width: '110px', paddingLeft: '5px', paddingRight: '5px', marginRight: '5px' }}
        >
          <FormattedMessage id="insertCustName" />
        </button>
        <button
          id="via-button-quill"
          className="ql-addLink"
          style={showError ? { borderColor: 'red', borderStyle: 'dashed', width: '110px', paddingLeft: '5px', paddingRight: '5px' } : { width: '110px', paddingLeft: '5px', paddingRight: '5px' }}
        >
          <FormattedMessage id="insertInspLink" />
        </button>
        <button
            id="via-button-quill"
            className="ql-addLocationLink"
            style={{ width: '110px', paddingLeft: '5px', paddingRight: '5px', marginLeft: '5px' }}
        >
          <FormattedMessage id="insertEmailParam" />
        </button>
    </div>
  ), []);

  // Not sure if this needs to be a useMemo, but its similar to modules so I'm not risking it.
  const formats = useMemo(() => ([
    'bold', 'italic', 'underline',
    'list', 'bullet', 'indent',
    'link',
  ]), []);


  if (quillRef.current) {
    // For some reason, even with images removed from the formats list, you can still drag and drop them into the editor.
    // This prevents such shenanigans.
    quillRef.current.editor.container.ondragover = () => false;
  }

  // They got here through mischief and deciet. Away with you foul demon!
  if (!emailType || emailType !== 'inspection_report'.toUpperCase()) {
    return <Redirect to={routeLocations.notFound} />;
  }

  // Standard admin check
  if (!userIsAdmin(sessionUser)) {
    return (
      <Result
        status="403"
        title={<FormattedMessage id="accessRestricted" />}
        subTitle={<FormattedMessage id="insufficientPermissions" />}
      />
    );
  }

  // The general table format has to be hard coded since we cannot parse half of a html tag
  // I have set it up so that email templates have
  // the 'template' which is what has been hardcoded here,
  // the 'header' and the 'footer' which are expected to be table rows.
  // I.E if we change any of the styles here on the database, tehy wont be reflected. But at least it looks pretty!
  // Technically I imagine we could dynamically generate this file and actually use the full template, but that seems so overkill.
  return (
    <Form onSubmit={e => handleSubmit(e)}>
      <PageHeader
        style={{ width: '100%' }}
        onBack={() => history.push(routeLocations.configure_email_content)}
        title={(<Text><FormattedMessage id="editReportEmail" /></Text>)}
      />
      <ViaValidationAlert validationError={missingLinkError} className="quill-style" />
      <div className="quill-style" style={{ marginTop: missingLinkError ? '0' : '40px' }}>
        { userGroup && currentHtml ? (
          <table style={{
            margin: 0,
            padding: 0,
            fontSize: '100%',
            lineHeight: '1.65',
            width: '100%',
            height: '100%',
            background: '#f8f8f8',
          }}
          >
            <tbody>
              <tr>
                <td align="center" style={{ width: '580px', display: 'block', margin: '0 auto' }}>
                  <table>
                    <tbody>
                      {ReactHtmlParser(visibleHeader)}
                      <tr style={{
                        borderCollapse: 'collapse',
                        display: 'block',
                        clear: 'both',
                        margin: '0 auto',
                        width: '585px',
                      }}
                      >
                        <td style={{ background: 'white', width: '585px' }}>
                          <CustomToolbar showError={missingLinkError} />
                          <ReactQuill
                            style={{ margin: '0px' }}
                            ref={quillRef}
                            formats={formats}
                            value={currentHtml}
                            modules={modules}
                            onChange={(content, delta, source, editor) => { setCurrentHtml(editor.getHTML()); }}
                          />
                        </td>
                      </tr>
                      {ReactHtmlParser(visibleFooter)}
                    </tbody>
                  </table>
                </td>
              </tr>
            </tbody>
          </table>
        ) : <Spin />
        }
        <Form.Item>
          <Row type="flex" justify="end" className="contact-details-content-row" style={{ margin: '60px' }}>
            <Button
              style={{ marginRight: '1em' }}
              loading={!emailContentMap[emailType.toUpperCase()]}
              disabled={!emailContentMap[emailType.toUpperCase()] || emailContentMap[emailType.toUpperCase()].defaultContent === currentHtml}
              onClick={() => { setCurrentHtml(emailContentMap[emailType.toUpperCase()].defaultContent); }}
            >
              <FormattedMessage id="revertToDefault" />
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={!emailContentMap[emailType.toUpperCase()]}
            >
              <FormattedMessage id="save" />
            </Button>
          </Row>
        </Form.Item>
      </div>
    </Form>
  );
}
EditEmailContent.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  loadUserGroup: PropTypes.func.isRequired,
  loadPageData: PropTypes.func.isRequired,
  emailContentMap: PropTypes.objectOf(PropTypes.shape({
    emailType: PropTypes.string.isRequired,
    emailContent: PropTypes.string.isRequired,
    header: PropTypes.string.isRequired,
    footer: PropTypes.string.isRequired,
    defaultContent: PropTypes.string,
  })),
  form: PropTypes.shape({
    validateFields: PropTypes.func.isRequired,
    getFieldDecorator: PropTypes.func.isRequired,
  }).isRequired,
  error: PropTypes.shape({
    errorMessage: PropTypes.string.isRequired,
    code: PropTypes.number.isRequired,
    errors: PropTypes.arrayOf(PropTypes.string),
  }),
  sessionUser: PropTypes.shape({
    linkedRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  history:
    PropTypes.shape({
      push: PropTypes.func.isRequired,
      goBack: PropTypes.func.isRequired,
    }).isRequired,
  saveContent: PropTypes.func.isRequired,
  userGroup: PropTypes.shape({
    description: PropTypes.string.isRequired,
    address: PropTypes.shape({
      line1: PropTypes.string.isRequired,
      line2: PropTypes.string,
      line3: PropTypes.string,
      line4: PropTypes.string,
      region: PropTypes.string,
      zipPostcode: PropTypes.string,
      country: PropTypes.string.isRequired,
    }),
    logo: PropTypes.string,
    logoType: PropTypes.string,
  }),
};

EditEmailContent.defaultProps = {
  emailContentMap: null,
  error: null,
  userGroup: null,
};

/**
 * A function for connecting redux state to the component props.
 */
const mapStateToProps = (state) => {
  const updateRequest = selectUpdateGroupRequest(state);
  return {
    error: updateRequest ? updateRequest.error : null,
    emailContentMap: selectEmailContentMap(state),
    loading: updateRequest ? updateRequest.isLoading : false,
    sessionUser: selectSessionUser(state),
    userGroup: selectUserGroup(state),
  };
};

/**
 * A function for connecting redux action to the component props.
 */
const mapDispatchToProps = dispatch => ({
  loadPageData: emailType => dispatch(requestSpecificEmailContent(emailType)),
  saveContent: (emailContent, history) => dispatch(requestSaveEmailContent(emailContent, history)),
  loadUserGroup: () => dispatch(requestUserGroupDetails()),
});

/**
 * Connect the redux state to our component.
 */
export default connect(mapStateToProps, mapDispatchToProps)(
  injectIntl(
    withRouter(
      Form.create({ name: 'edit_contact_details' })(EditEmailContent),
    ),
  ),
);
